/**
 * Help — searchable hub of all in-app help topics.
 *
 * Lives at /help (in-app, behind PrivateRoute so staff hit the right
 * topics for their role). Each card deep-links to /help/<slug>; the
 * search box filters by title, blurb, keywords and body text.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { HELP_TOPICS, searchHelp } from "@/lib/helpContent";

export default function Help() {
  const [q, setQ] = useState("");
  const results = useMemo(() => searchHelp(q), [q]);

  return (
    <div className="animate-fade-in" data-testid="help-hub">
      <PageHeader
        title="Help"
        subtitle="How to run a HACCP-AU kitchen — temperature logs, deliveries, labels, audit packs and more."
      />

      <div className="surface p-5 mb-6 max-w-2xl">
        <div className="flex items-center gap-3">
          <Search className="w-5 h-5 text-eucalyptus-700" />
          <Input
            data-testid="help-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search help — try 'fridge', 'audit pack' or 'offline'…"
            className="h-12 border-0 shadow-none focus-visible:ring-0 px-0 font-medium"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {results.length === 0 && (
          <div className="surface p-8 text-center text-charcoal-soft md:col-span-2" data-testid="help-no-results">
            Nothing matches &ldquo;{q}&rdquo;. Try a broader term.
          </div>
        )}
        {results.map((topic) => {
          const Icon = topic.icon;
          return (
            <Link
              key={topic.slug}
              to={`/help/${topic.slug}`}
              data-testid={`help-card-${topic.slug}`}
              className="surface p-5 hover:shadow-md transition-shadow flex gap-4 items-start group"
            >
              <div className="w-10 h-10 rounded-md bg-eucalyptus-100 text-eucalyptus-900 grid place-items-center shrink-0 group-hover:bg-eucalyptus-700 group-hover:text-white transition-colors">
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-base group-hover:text-eucalyptus-900">
                  {topic.title}
                </div>
                <p className="text-sm text-charcoal-soft mt-1">{topic.blurb}</p>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-8 surface-alt p-5 text-sm text-charcoal-soft max-w-2xl">
        Can&apos;t find what you need? Email{" "}
        <a className="underline text-eucalyptus-900 font-medium" href="mailto:support@haccp.au">
          support@haccp.au
        </a>
        {" "}— we usually reply within one business day (AEST).
      </div>
    </div>
  );
}
